import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {QlService} from 'features/core/shared/ql/service/ql.service';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {ProvisionFormerContractRequirement} from '../../enum/provision-former-contract-requirement.enum';

/**
 * AI:
 * Status: "in progress"
 * Type: Service
 * SubType: DataProvider
 * Reason: Probes, with one cheap QL query, which of the 13 contract dimensions are actually filled
 *     in the CONDITIONS (cp.ProvisionAssignmentCondition) of ONE provision type — optionally of ONE
 *     condition set. Since the assignment-condition redesign the dimensions live only on the
 *     condition rows (the six assignment entities merely reference a condition set), so the column
 *     reduction of 04-frontend.md §6 applies to the condition table of the navItem
 *     "Bedingungssätze". Returns the used dimension ATTRIBUTES; the caller maps them to its own
 *     explicit-join columns. A failing or empty probe always falls back to the complete list, so
 *     the condition table can never end up without its dimension columns.
 */

/**
 * The 13 dimensions of `cp.ProvisionAssignmentCondition` as QL attributes (the relation id or the
 * plain column), in the order 02-datenmodell.md §1 declares them. Read left to right this is also
 * the reading order of the reduced table: the contract dimensions first, then the term window,
 * then the former-contract block (dimensions 11–13, E1).
 */
export const CP_ALL_DIMENSION_ATTRIBUTES: string[] = [
    'groupId',
    'productId',
    'partnerId',
    'contractClosureChannelTypeId',
    'contractOriginTypeId',
    'contractBaseCategoryId',
    'contractBaseTypeId',
    'categoryId',
    'contractTypeId',
    'termMonthsFrom',
    'termMonthsTo',
    'formerContractRequirement',
    'formerContractPartnerId',
    'formerTermMonthsFrom',
    'formerTermMonthsTo'
];

/** The entity that carries the 13 dimensions since the condition-set redesign. */
const CP_CONDITION_ENTITY = 'cp.ProvisionAssignmentCondition';

/**
 * Probe behind the column reduction of the condition table (04-frontend.md §6).
 *
 * ## The problem it solves
 * A condition carries 13 dimensions plus priority, effect and description. A table that wide is not
 * operable, and in practice a provision type only ever uses a handful of the dimensions; the rest
 * is NULL in every single condition. 04 §6 therefore makes the table show ONLY the filled
 * dimensions.
 *
 * "Filled" is a property of the DATA, not of the configuration, so it has to be asked. This
 * service asks it with ONE cheap QL query: the dimension attributes of the conditions of exactly
 * one provision type, no joins, no FK text resolution. The caller maps the answered attributes to
 * the columns of its explicit-join query (`CP_CONDITION_DIMENSION_COLUMNS`).
 *
 * ## Wildcard semantics (04 §6, A1-03)
 * A NULL dimension is a wildcard and counts as empty. `formerContractRequirement` is the one
 * dimension that is NOT nullable — its wildcard is the enum constant `ANY`, and 04 §6 says
 * explicitly that `formerContractRequirement = ANY` counts as empty too. The check below therefore
 * treats both NULL and the ANY ordinal as empty, and accepts the value as an ordinal or as the
 * enum name (the QL pipe may deliver either).
 *
 * ## Failure behaviour
 * A failing probe must never blank out the condition table. On any error, and for an empty result,
 * the answer is {@link CP_ALL_DIMENSION_ATTRIBUTES} — the wide, but complete, table. Errors are
 * swallowed HERE on purpose (`catchError`) instead of surfacing as a red toast: the probe is an
 * internal layout optimisation, not a user action, and its fallback is a correct (only wider)
 * table.
 */
@Injectable({providedIn: 'root'})
export class CpProvisionTypeDimensionService {

    constructor(private qlService: QlService) {
    }

    /**
     * Determine which dimensions are filled in at least one condition of the given provision type.
     *
     * @param provisionTypeId  the provision type whose conditions are inspected
     * @param conditionSetId   optional: restrict the probe to one condition set
     * @returns the QL attribute names of the used dimensions, in the declaration order of
     *          02-datenmodell.md §1; the complete list when the probe fails or the table is empty
     */
    getUsedDimensionAttributes(provisionTypeId: number, conditionSetId?: number): Observable<string[]> {

        if (!provisionTypeId) {
            return of(CP_ALL_DIMENSION_ATTRIBUTES);
        }

        const queryName = 'cpUsedDimensions';

        return this.qlService.builder()
            .showErrors(false)
            .query({
                name: queryName,
                type: 'view',
                start: {
                    type: 'entity',
                    name: CP_CONDITION_ENTITY,
                    as: 'e',
                    fields: [
                        {name: 'id'},
                        ...CP_ALL_DIMENSION_ATTRIBUTES.map(attribute => ({name: attribute}))
                    ]
                },
                filters: [
                    FilterCriteria.create('e.provisionTypeId', FilterCriteria.cOperatorEqual, provisionTypeId),
                    ...(conditionSetId
                        ? [FilterCriteria.create('e.provisionAssignmentConditionSetId',
                            FilterCriteria.cOperatorEqual, conditionSetId)]
                        : [])
                ]
            })
            .execute()
            .pipe(
                map(result => this.evaluate(result.get(queryName))),
                catchError(() => of(CP_ALL_DIMENSION_ATTRIBUTES))
            );
    }

    /**
     * Reduce the probed rows to the used dimensions, as ATTRIBUTE names.
     *
     * An EMPTY table yields the complete list on purpose: a provision type without a single
     * condition would otherwise show a table with no dimension column at all, and the user
     * could not tell what the rows they are about to create even consist of.
     */
    private evaluate(rows: any[]): string[] {

        if (!rows || !rows.length) {
            return CP_ALL_DIMENSION_ATTRIBUTES;
        }

        const used: string[] = [];

        for (const attribute of CP_ALL_DIMENSION_ATTRIBUTES) {
            // A QL response keys a root-entity column as `e.<attribute>`.
            const key = 'e.' + attribute;
            const isFormerContractRequirement = attribute === 'formerContractRequirement';

            const filled = rows.some(row => isFormerContractRequirement
                ? this.isFormerContractRequirementFilled(row?.[key])
                : this.isFilled(row?.[key]));

            if (filled) {
                used.push(attribute);
            }
        }

        // Every dimension NULL in every row = the pure default row set. Showing no dimension at
        // all would leave a table of priorities and results with nothing to explain them, so the
        // complete list wins again.
        return used.length ? used : CP_ALL_DIMENSION_ATTRIBUTES;
    }

    /** NULL, undefined and the empty string are the wildcard; `0` is a real value and stays. */
    private isFilled(value: any): boolean {
        return value !== null && value !== undefined && value !== '';
    }

    /**
     * `formerContractRequirement` is non-nullable, so its wildcard is the enum constant ANY
     * (04 §6: "`formerContractRequirement = ANY` zählt als leer"). Accepts the ordinal and the
     * enum name, because the QL pipe may deliver either.
     */
    private isFormerContractRequirementFilled(value: any): boolean {

        if (!this.isFilled(value)) {
            return false;
        }
        if (typeof value === 'number') {
            return value !== ProvisionFormerContractRequirement.ANY;
        }
        if (typeof value === 'string') {
            const normalized = value.trim().toUpperCase();
            return normalized !== 'ANY'
                && normalized !== String(ProvisionFormerContractRequirement.ANY);
        }
        return true;
    }

}
